import * as d3 from "d3";
import type { QuarterEmissions } from "../../../apiClient/generated";

export interface StackDataPoint {
    quarter: string;
    totalCount: number;
    _counts: Record<string, number>; // Raw counts for tooltips
}

/**
 * Extract all unique provider names from emissions data, sorted by total emission count (descending).
 * Providers with higher total emissions appear first and get assigned earlier colors in the palette.
 * Alphabetical order is used as a tie-breaker for consistent ordering.
 */
export function getAllProviders(data: QuarterEmissions[]): string[] {
    // Calculate total emission count per provider across all quarters
    const providerTotals = new Map<string, number>();

    data.forEach((quarter) => {
        quarter.data.forEach((provider) => {
            const currentTotal =
                providerTotals.get(provider.dataProviderName) || 0;
            providerTotals.set(
                provider.dataProviderName,
                currentTotal + provider.count,
            );
        });
    });

    // Sort by total count (descending), then alphabetically as tie-breaker
    return Array.from(providerTotals.keys()).sort((a, b) => {
        const countDiff = providerTotals.get(b)! - providerTotals.get(a)!;
        if (countDiff !== 0) return countDiff;
        return a.localeCompare(b); // Alphabetical tie-breaker
    });
}

/**
 * Transform QuarterEmissions[] into format suitable for d3.stack()
 * Each quarter becomes an object with provider counts as properties.
 */
export function transformToStackData(
    data: QuarterEmissions[],
    allProviders: string[],
): StackDataPoint[] {
    return data.map((quarter) => {
        const totalCount = quarter.data.reduce((sum, p) => sum + p.count, 0);

        const obj: StackDataPoint = {
            quarter: quarter.quarter,
            totalCount: totalCount,
            _counts: {},
        };

        // Add each provider's count (sum if provider appears multiple times)
        allProviders.forEach((providerName) => {
            const count = quarter.data
                .filter((p) => p.dataProviderName === providerName)
                .reduce((sum, p) => sum + p.count, 0);
            obj._counts[providerName] = count;
        });

        return obj;
    });
}

/**
 * Creates a D3 ordinal color scale for provider visualization
 */
export function createProviderColorScale(
    providers: string[],
): d3.ScaleOrdinal<string, string> {
    const colors = [
        "#6194AA",
        "#315466",
        "#486B2E",
        "#869F52",
        "#EC8536",
        "#D85052",
        "#A47095",
        "#FB959F",
        "#E9C24F",
        "#906A57",
    ];
    return d3.scaleOrdinal<string>().domain(providers).range(colors);
}
